import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { FutureDatePicker, MonthPicker } from '../components/ui/date-picker';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Target, Calendar, TrendingUp, BookCheck, Clock, Plus, Trash2, Lock, Sparkles, RotateCcw, FileText, MessageSquare, FolderTree, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import SubscriptionGate from '../components/common/SubscriptionGate';
import { toast } from 'sonner';
import { format } from 'date-fns';
import UpgradeModal from '../components/common/UpgradeModal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ModuleAccessOverlay from '../components/common/ModuleAccessOverlay';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to safely format exam_month for display
const formatExamMonth = (examMonth, options = { year: 'numeric', month: 'long' }) => {
  if (!examMonth || examMonth.trim() === '') {
    return 'Unknown Date';
  }
  
  const trimmed = examMonth.trim();
  
  // Try YYYY-MM format first
  if (/^\d{4}-\d{2}$/.test(trimmed)) {
    const date = new Date(trimmed + '-01');
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', options);
    }
  }
  
  // Try "Month YYYY" format (e.g., "December 2025")
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'];
  const monthYearMatch = trimmed.match(/^(\w+)\s+(\d{4})$/);
  if (monthYearMatch) {
    const monthName = monthYearMatch[1];
    const year = monthYearMatch[2];
    const monthIndex = months.findIndex(m => m.toLowerCase() === monthName.toLowerCase());
    if (monthIndex !== -1) {
      const date = new Date(parseInt(year), monthIndex, 1);
      return date.toLocaleDateString('en-US', options);
    }
  }
  
  return 'Unknown Date';
};

const DATE_RANGE_OPTIONS = [
  { value: '1m', label: 'Last 1 Month' },
  { value: '3m', label: 'Last 3 Months' },
  { value: '6m', label: 'Last 6 Months' },
  { value: '1y', label: 'Last 1 Year' },
  { value: 'all', label: 'All Time' }
];

const ProgressTrackerPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { preferences } = usePreferences();
  
  // Module access check
  const { getProgressTrackerStatus, loading: moduleLoading } = useModuleAccess();
  const progressStatus = getProgressTrackerStatus();
  
  const [examPlans, setExamPlans] = useState([]);
  const [oralFunctions, setOralFunctions] = useState([]);
  const [writtenFunctions, setWrittenFunctions] = useState([]);
  const [selectedFunction, setSelectedFunction] = useState('');
  const [selectedFunctionType, setSelectedFunctionType] = useState(null); // 'oral' or 'written'
  const [dateRange, setDateRange] = useState('6m'); // Date range for written analytics
  const [analytics, setAnalytics] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newPlan, setNewPlan] = useState({ function_id: '', exam_month: '', exam_date: '', function_type: 'oral' });
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [expandedTopics, setExpandedTopics] = useState(new Set());

  // Organize topics into hierarchical structure with aggregated stats
  const hierarchicalTopicProgress = useMemo(() => {
    if (!analytics?.topic_progress) return [];
    
    const topics = analytics.topic_progress;

    // Recursive tree build (iter 105.3): backend now returns every
    // ancestor of any topic with questions (walk-to-root). Previously
    // this hierarchy builder only linked one level — grandchild+
    // topics silently disappeared. Now: group by parent_topic_id, walk
    // depth-first, aggregate stats from leaves up.
    const noTopicEntry = topics.find(t => t.topic_id === null);
    const nonRoot = topics.filter(t => t.topic_id !== null);
    const byParent = new Map();
    nonRoot.forEach((t) => {
      const p = t.parent_topic_id || null;
      if (!byParent.has(p)) byParent.set(p, []);
      byParent.get(p).push(t);
    });

    const buildNode = (node) => {
      const rawChildren = byParent.get(node.topic_id) || [];
      const children = rawChildren.map(buildNode);
      const aggregatedTotal =
        node.total + children.reduce((s, c) => s + c.aggregatedTotal, 0);
      const aggregatedCompleted =
        node.completed + children.reduce((s, c) => s + c.aggregatedCompleted, 0);
      const aggregatedPercentage = aggregatedTotal > 0
        ? Math.round((aggregatedCompleted / aggregatedTotal) * 100)
        : 0;
      return {
        ...node,
        isParent: children.length > 0,
        hasChildren: children.length > 0,
        children,
        aggregatedTotal,
        aggregatedCompleted,
        aggregatedPercentage,
      };
    };

    const hierarchy = (byParent.get(null) || []).map(buildNode);

    // "Questions without Topic" pinned at the end when present.
    if (noTopicEntry) {
      hierarchy.push({
        ...noTopicEntry,
        topic_name: 'Questions without Topic',
        isParent: false,
        hasChildren: false,
        children: [],
        aggregatedTotal: noTopicEntry.total,
        aggregatedCompleted: noTopicEntry.completed,
        aggregatedPercentage: Math.round(noTopicEntry.percentage),
      });
    }

    return hierarchy;
  }, [analytics?.topic_progress]);

  const toggleTopicExpanded = (topicId) => {
    setExpandedTopics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(topicId)) {
        newSet.delete(topicId);
      } else {
        newSet.add(topicId);
      }
      return newSet;
    });
  };

  const checkSubscription = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/subscription`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to check subscription:', error);
    }
  }, []);

  const fetchFunctions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/functions`);
      // Separate oral and written functions
      const oral = response.data.filter(f => 
        f.grade === preferences.grade && f.exam_type !== 'written'
      );
      const written = response.data.filter(f => 
        f.grade === preferences.grade && f.exam_type === 'written'
      );
      setOralFunctions(oral);
      setWrittenFunctions(written);
    } catch (error) {
      console.error('Failed to fetch functions:', error);
    }
  }, [preferences.grade]);

  const fetchExamPlans = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/exam-plans`);
      setExamPlans(response.data);
      
      // Auto-select first function if available
      if (response.data.length > 0 && !selectedFunction) {
        const firstPlan = response.data[0];
        setSelectedFunction(firstPlan.function_id);
        setSelectedFunctionType(firstPlan.function_type || 'oral');
      }
    } catch (error) {
      console.error('Failed to fetch exam plans:', error);
      toast.error('Failed to load exam plans');
    } finally {
      setLoading(false);
    }
  }, [selectedFunction]);

  const fetchAnalytics = useCallback(async () => {
    if (!selectedFunction || !selectedFunctionType) return;
    
    try {
      setAnalyticsLoading(true);
      
      let response;
      if (selectedFunctionType === 'written') {
        response = await axios.get(`${API}/progress/written/analytics/${selectedFunction}`, {
          params: { date_range: dateRange }
        });
      } else {
        response = await axios.get(`${API}/progress/analytics/${selectedFunction}`, {
          params: {
            grade: preferences.grade,
            mmd_location: preferences.mmd_location
          }
        });
      }
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  }, [selectedFunction, selectedFunctionType, dateRange, preferences.grade, preferences.mmd_location]);

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated()) {
      toast.error('Please login to access Progress Tracker');
      navigate('/');
      return;
    }

    if (!preferences.grade || !preferences.mmd_location) {
      // Skip fetches — the render layer shows a friendly empty-state card
      // instead of a hard redirect (consistent with Notebook/Insights UX).
      return;
    }

    fetchFunctions();
    fetchExamPlans();
    checkSubscription();
  }, [authLoading, isAuthenticated, navigate, preferences.grade, preferences.mmd_location, fetchFunctions, fetchExamPlans, checkSubscription]);

  useEffect(() => {
    if (selectedFunction && selectedFunctionType) {
      fetchAnalytics();
    }
  }, [selectedFunction, selectedFunctionType, fetchAnalytics]);

  const handleAddPlan = async () => {
    if (!newPlan.function_id || !newPlan.exam_month) {
      toast.error('Please select function and exam month');
      return;
    }

    try {
      await axios.post(`${API}/exam-plans`, {
        ...newPlan,
        function_type: newPlan.function_type
      });
      toast.success('Exam plan added successfully');
      setShowAddDialog(false);
      setNewPlan({ function_id: '', exam_month: '', exam_date: '', function_type: 'oral' });
      fetchExamPlans();
    } catch (error) {
      console.error('Failed to add exam plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to add exam plan');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this exam plan?')) return;

    try {
      await axios.delete(`${API}/exam-plans/${planId}`);
      toast.success('Exam plan deleted');
      fetchExamPlans();
      const deletedPlan = examPlans.find(p => p.id === planId);
      if (selectedFunction === deletedPlan?.function_id) {
        setSelectedFunction('');
        setSelectedFunctionType(null);
        setAnalytics(null);
      }
    } catch (error) {
      console.error('Failed to delete exam plan:', error);
      toast.error('Failed to delete exam plan');
    }
  };

  const handleUpdateDate = async (planId, newDate) => {
    if (!newDate) {
      toast.error('Please select a date');
      return;
    }

    try {
      const response = await axios.put(`${API}/exam-plans/${planId}`, { exam_date: newDate });
      setExamPlans(prevPlans => 
        prevPlans.map(plan => plan.id === planId ? response.data : plan)
      );
      toast.success('Exam date updated successfully');
      
      const updatedPlan = examPlans.find(p => p.id === planId);
      if (updatedPlan && selectedFunction === updatedPlan.function_id) {
        await fetchAnalytics();
      }
    } catch (error) {
      console.error('Failed to update date:', error);
      toast.error(error.response?.data?.detail || 'Failed to update date');
    }
  };

  const handleResetCompleted = async () => {
    if (!selectedFunction || !selectedFunctionType) return;
    
    const confirmMsg = selectedFunctionType === 'written' 
      ? 'Are you sure you want to reset all completed progress for this written function?'
      : 'Are you sure you want to reset all completed progress for this function? This will mark all questions as not completed. Viewed and bookmarked status will be preserved.';
    
    if (!window.confirm(confirmMsg)) return;

    try {
      let response;
      if (selectedFunctionType === 'written') {
        response = await axios.post(`${API}/progress/written/reset-completed/${selectedFunction}`);
      } else {
        response = await axios.post(`${API}/progress/reset-completed/${selectedFunction}`, null, {
          params: {
            grade: preferences.grade,
            mmd_location: preferences.mmd_location
          }
        });
      }
      toast.success(response.data.message || 'Progress reset successfully');
      await fetchAnalytics();
    } catch (error) {
      console.error('Failed to reset progress:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset progress');
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedFunction(plan.function_id);
    setSelectedFunctionType(plan.function_type || 'oral');
  };

  const calculateCountdown = (examMonth, examDate) => {
    const today = new Date();
    let targetDate;
    
    if (examDate) {
      targetDate = new Date(examDate);
    } else {
      const [year, month] = examMonth.split('-');
      targetDate = new Date(parseInt(year), parseInt(month) - 1, 20);
    }
    
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return { days: diffDays, date: targetDate };
  };

  const getFunctionName = (functionId, functionType) => {
    const allFunctions = functionType === 'written' ? writtenFunctions : oralFunctions;
    const func = allFunctions.find(f => f.id === functionId);
    if (!func) {
      // Try both lists as fallback
      const fallback = [...oralFunctions, ...writtenFunctions].find(f => f.id === functionId);
      if (!fallback) return 'Unknown';
      const suffix = fallback.function_suffix && fallback.function_suffix !== 'None' && fallback.function_suffix !== 'null' ? fallback.function_suffix : '';
      return `F${fallback.function_number}${suffix} - ${fallback.name}`;
    }
    const suffix = func.function_suffix && func.function_suffix !== 'None' && func.function_suffix !== 'null' ? func.function_suffix : '';
    return `F${func.function_number}${suffix} - ${func.name}`;
  };

  const getGrade = (pct) => {
    if (pct >= 90) return { grade: 'A+', color: 'text-emerald-600', bg: 'bg-emerald-100' };
    if (pct >= 80) return { grade: 'A', color: 'text-green-600', bg: 'bg-green-100' };
    if (pct >= 70) return { grade: 'B', color: 'text-blue-600', bg: 'bg-blue-100' };
    if (pct >= 60) return { grade: 'C', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    if (pct >= 50) return { grade: 'D', color: 'text-orange-600', bg: 'bg-orange-100' };
    return { grade: 'F', color: 'text-red-600', bg: 'bg-red-100' };
  };

  // Check access - use the new subscription access hook
  const { canAccessProgressTracker, loading: subscriptionLoading } = useSubscriptionAccess();
  const subscribedGrade = subscriptionStatus?.subscribed_grade;
  const userRole = subscriptionStatus?.user_role;
  const currentGrade = preferences.grade;
  const isAdmin = userRole === 'admin' || userRole === 'sub_admin';
  const hasGradeAccess = isAdmin || subscribedGrade === 'all' || subscribedGrade === currentGrade;
  
  // Access requires: module access from plan AND grade access (admins bypass both)
  const hasAccess = isAdmin || (canAccessProgressTracker() && hasGradeAccess);

  if (authLoading || subscriptionLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  // Get available functions for adding (not already in plans)
  const availableOralFunctions = oralFunctions.filter(f => !examPlans.find(p => p.function_id === f.id));
  const availableWrittenFunctions = writtenFunctions.filter(f => !examPlans.find(p => p.function_id === f.id));

  // Module access enforcement - show overlay if progress tracker is not active
  if (!moduleLoading && progressStatus && progressStatus !== 'active') {
    return (
      <Layout>
        <ModuleAccessOverlay
          status={progressStatus}
          featureName="Progress Tracker"
          description={
            progressStatus === 'coming_soon'
              ? "Progress Tracker is coming soon! We're working on bringing you detailed analytics and progress tracking for your exam preparation."
              : "Progress Tracker is currently not available. Please check back later."
          }
        />
      </Layout>
    );
  }

  // Grade / MMD unset — friendly empty-state card, matches Notebook/Insights UX.
  if (isAuthenticated() && (!preferences.grade || !preferences.mmd_location)) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto text-center py-16" data-testid="progress-grade-unset">
          <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">Set your grade & MMD first</h2>
          <p className="text-gray-500 mb-6">
            Please set your grade and MMD location in your profile to see your progress analytics.
          </p>
          <Button onClick={() => navigate('/profile')} className="bg-[#10B981] hover:bg-[#0F9673]" data-testid="progress-open-profile-btn">
            Go to Profile
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-transition max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#10B981]/10 rounded-lg">
              <Target className="w-6 h-6 text-[#10B981]" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Progress Tracker</h1>
              <p className="text-gray-600">
                Track your preparation for {preferences.grade} - {preferences.mmd_location}
              </p>
            </div>
          </div>
        </div>

        {!hasAccess ? (
          <SubscriptionGate
            hasAccess={false}
            featureName="Progress Tracker"
            moduleKey="progress_tracker"
            subscribedGrade={subscribedGrade}
            targetGrade={preferences.grade}
            description="Track your exam preparation progress, set target dates, view analytics, and monitor your performance across all functions with the premium Progress Tracker."
          />
        ) : (
          <>
            {/* Exam Plans */}
            <Card className="mb-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Your Exam Schedule
                  </CardTitle>
                  <CardDescription>
                    Plan your exam dates for oral and written functions
                  </CardDescription>
                </div>
                {(availableOralFunctions.length > 0 || availableWrittenFunctions.length > 0) && (
                  <Button
                    onClick={() => setShowAddDialog(true)}
                    className="bg-[#10B981] hover:bg-[#10B981]/90"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Plan
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="spinner"></div>
                  </div>
                ) : examPlans.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No Exam Plans Yet</h3>
                    <p className="text-gray-500 mb-4">
                      Add your exam schedule to track your preparation progress
                    </p>
                    <Button
                      onClick={() => setShowAddDialog(true)}
                      className="bg-[#10B981] hover:bg-[#10B981]/90"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Your First Exam
                    </Button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {examPlans.map((plan) => {
                      const countdown = calculateCountdown(plan.exam_month, plan.exam_date);
                      const isSelected = selectedFunction === plan.function_id;
                      const isWritten = plan.function_type === 'written';
                      
                      return (
                        <Card
                          key={plan.id}
                          className={`cursor-pointer transition-all ${
                            isSelected 
                              ? isWritten 
                                ? 'border-[#0A7EA4] bg-cyan-50/50' 
                                : 'border-[#10B981] bg-green-50/50'
                              : 'hover:shadow-md'
                          }`}
                          onClick={() => handleSelectPlan(plan)}
                        >
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start mb-3 gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  {isWritten ? (
                                    <FileText className="w-4 h-4 text-[#0A7EA4]" />
                                  ) : (
                                    <MessageSquare className="w-4 h-4 text-[#8B5CF6]" />
                                  )}
                                  <Badge className={isWritten ? 'bg-[#0A7EA4] text-xs' : 'bg-[#8B5CF6] text-xs'}>
                                    {isWritten ? 'Written' : 'Oral'}
                                  </Badge>
                                </div>
                                <h4 className="font-semibold text-gray-900 mb-1 break-words">
                                  {getFunctionName(plan.function_id, plan.function_type)}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  {formatExamMonth(plan.exam_month)}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePlan(plan.id);
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>

                            {/* Countdown */}
                            <div className={`p-3 rounded-lg mb-3 ${
                              countdown.days < 0 
                                ? 'bg-gray-100' 
                                : countdown.days <= 30 
                                  ? 'bg-red-50 border border-red-200' 
                                  : 'bg-blue-50 border border-blue-200'
                            }`}>
                              <div className="flex items-center gap-2 mb-1">
                                <Clock className={`w-4 h-4 ${
                                  countdown.days < 0 
                                    ? 'text-gray-500' 
                                    : countdown.days <= 30 
                                      ? 'text-red-600' 
                                      : 'text-blue-600'
                                }`} />
                                <span className={`font-bold ${
                                  countdown.days < 0 
                                    ? 'text-gray-600' 
                                    : countdown.days <= 30 
                                      ? 'text-red-600' 
                                      : 'text-blue-600'
                                }`}>
                                  {countdown.days < 0 
                                    ? 'Exam Passed' 
                                    : countdown.days === 0 
                                      ? 'Today!' 
                                      : `${countdown.days} days`}
                                </span>
                              </div>
                              <p className="text-xs text-gray-600">
                                {plan.is_date_confirmed ? (
                                  <>
                                    Exam: {countdown.date.toLocaleDateString('en-US', { 
                                      month: 'short', 
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </>
                                ) : (
                                  <>
                                    Tentative: {countdown.date.toLocaleDateString('en-US', { 
                                      month: 'short', 
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                    <br />
                                    <span className="text-orange-600">⚠ Set exact date below</span>
                                  </>
                                )}
                              </p>
                            </div>

                            {!plan.is_date_confirmed && (
                              <div onClick={(e) => e.stopPropagation()}>
                                <FutureDatePicker
                                  date={null}
                                  onDateChange={(date) => date && handleUpdateDate(plan.id, format(date, 'yyyy-MM-dd'))}
                                  placeholder="Set exact date"
                                  className="w-full text-xs h-8"
                                />
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Analytics Loading */}
            {analyticsLoading && selectedFunction && (
              <div className="flex justify-center py-12">
                <LoadingSpinner message="Loading analytics..." />
              </div>
            )}

            {/* Analytics Section */}
            {selectedFunction && analytics && !analyticsLoading && (
              <>
                <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <TrendingUp className={`w-5 h-5 ${selectedFunctionType === 'written' ? 'text-[#0A7EA4]' : 'text-[#10B981]'}`} />
                    Analytics for {getFunctionName(selectedFunction, selectedFunctionType)}
                    <Badge className={selectedFunctionType === 'written' ? 'bg-[#0A7EA4]' : 'bg-[#8B5CF6]'}>
                      {selectedFunctionType === 'written' ? 'Written' : 'Oral'}
                    </Badge>
                  </h2>
                  
                  {/* Date Range Selector - Only for Written */}
                  {selectedFunctionType === 'written' && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <Select value={dateRange} onValueChange={setDateRange}>
                        <SelectTrigger className="w-[160px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DATE_RANGE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Key Metrics */}
                <div className="grid md:grid-cols-4 gap-6 mb-6">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">
                        Questions Studied
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-[#0A7EA4]">
                        {selectedFunctionType === 'written' ? analytics.questions_studied : analytics.questions_studied}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        of {analytics.total_questions} available
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">Completion Rate</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-[#10B981]">
                        {analytics.completion_rate}%
                      </div>
                      <Progress value={analytics.completion_rate} className="mt-2 h-2" />
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">Practice Sessions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-[#8B5CF6]">
                        {selectedFunctionType === 'written' ? analytics.practice_sessions_count : analytics.practice_sessions_count}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Total attempts
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">Avg Score</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(selectedFunctionType === 'written' ? analytics.avg_percentage : analytics.avg_practice_score) !== null && 
                       (selectedFunctionType === 'written' ? analytics.avg_percentage : analytics.avg_practice_score) !== undefined ? (
                        <>
                          <div className={`text-3xl font-bold ${
                            (selectedFunctionType === 'written' ? analytics.avg_percentage : analytics.avg_practice_score) >= 60 
                              ? 'text-[#10B981]' 
                              : 'text-orange-500'
                          }`}>
                            {Math.round(selectedFunctionType === 'written' ? analytics.avg_percentage : analytics.avg_practice_score)}%
                          </div>
                          <Badge className={`mt-2 ${
                            (selectedFunctionType === 'written' ? analytics.avg_percentage : analytics.avg_practice_score) >= 60 
                              ? 'bg-[#10B981]' 
                              : 'bg-orange-500'
                          }`}>
                            {(selectedFunctionType === 'written' ? analytics.avg_percentage : analytics.avg_practice_score) >= 60 ? 'Passing' : 'Needs Work'}
                          </Badge>
                        </>
                      ) : (
                        <div className="text-2xl font-bold text-gray-400">N/A</div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Topic-wise Progress - Hierarchical */}
                {hierarchicalTopicProgress.length > 0 && (
                  <Card className="mb-6">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle>Topic-wise Completion Rate</CardTitle>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleResetCompleted}
                        className="text-orange-600 border-orange-300 hover:bg-orange-50"
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Reset Completed
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {hierarchicalTopicProgress.map((topic) => (
                          <div key={topic.topic_id || 'no-topic'}>
                            {/* Parent topic or standalone topic */}
                            {topic.hasChildren ? (
                              <Collapsible 
                                open={expandedTopics.has(topic.topic_id)}
                                onOpenChange={() => toggleTopicExpanded(topic.topic_id)}
                              >
                                <CollapsibleTrigger className="w-full text-left">
                                  <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                                    {expandedTopics.has(topic.topic_id) ? (
                                      <ChevronDown className="w-4 h-4 text-gray-500" />
                                    ) : (
                                      <ChevronRight className="w-4 h-4 text-gray-500" />
                                    )}
                                    <FolderTree className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex justify-between items-start gap-2 mb-1">
                                        <span className="text-sm font-medium text-gray-800 break-words min-w-0 flex-1 text-left">
                                          {topic.topic_name}
                                        </span>
                                        <span className="text-sm text-gray-600 flex-shrink-0 whitespace-nowrap">
                                          {topic.aggregatedCompleted} / {topic.aggregatedTotal} ({topic.aggregatedPercentage}%)
                                        </span>
                                      </div>
                                      <Progress 
                                        value={topic.aggregatedPercentage} 
                                        className={cn(
                                          "h-2",
                                          topic.aggregatedPercentage === 100 && "bg-green-100 [&>div]:bg-green-500"
                                        )} 
                                      />
                                    </div>
                                  </div>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="ml-10 mt-2 space-y-2 border-l-2 border-gray-200 pl-4">
                                    {/* iter 112: renamed from
                                        "General (no subtopic)" to the
                                        parent topic's own name, per
                                        product call — the row still
                                        represents the parent's own
                                        directly-tagged questions
                                        (~6% oral / ~12% written of
                                        the corpus). Indentation +
                                        progress bar carry the "this
                                        is the parent's own bucket"
                                        meaning; no extra styling
                                        needed. */}
                                    {topic.total > 0 && (
                                      <div className="py-1">
                                        <div className="flex justify-between items-center mb-1">
                                          <span className="text-sm text-gray-600">
                                            {topic.topic_name}
                                          </span>
                                          <span className="text-xs text-gray-500">
                                            {topic.completed} / {topic.total} ({Math.round(topic.percentage)}%)
                                          </span>
                                        </div>
                                        <Progress value={topic.percentage} className="h-1.5" />
                                      </div>
                                    )}
                                    {/* Show descendants recursively (iter 105.3):
                                        flatten the subtree into indented rows
                                        so grandchild+ topics render, not just
                                        direct children. Each row's paddingLeft
                                        scales with depth. */}
                                    {(() => {
                                      const rows = [];
                                      const walk = (nodes, depth) => {
                                        nodes.forEach((child) => {
                                          rows.push({ child, depth });
                                          if (child.children && child.children.length > 0) {
                                            walk(child.children, depth + 1);
                                          }
                                        });
                                      };
                                      walk(topic.children, 0);
                                      return rows.map(({ child, depth }) => (
                                        <div
                                          key={child.topic_id}
                                          className="py-1"
                                          style={{ paddingLeft: `${depth * 12}px` }}
                                        >
                                          <div className="flex justify-between items-start gap-2 mb-1">
                                            <span className="text-sm text-gray-600 flex items-start gap-1 min-w-0 flex-1 break-words">
                                              <span className="text-gray-400 flex-shrink-0">└</span>
                                              <span className="min-w-0 break-words">{child.topic_name}</span>
                                            </span>
                                            <span className="text-xs text-gray-500 flex-shrink-0 whitespace-nowrap">
                                              {child.completed} / {child.total} ({Math.round(child.percentage)}%)
                                            </span>
                                          </div>
                                          <Progress
                                            value={child.percentage}
                                            className={cn(
                                              "h-1.5",
                                              child.percentage === 100 && "bg-green-100 [&>div]:bg-green-500"
                                            )}
                                          />
                                        </div>
                                      ));
                                    })()}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            ) : (
                              /* Standalone topic (no children) */
                              <div className="p-2">
                                <div className="flex justify-between items-start gap-2 mb-2">
                                  <span className={cn(
                                    "text-sm font-medium min-w-0 flex-1 break-words",
                                    topic.topic_id === null ? "text-gray-500 italic" : "text-gray-700"
                                  )}>
                                    {topic.topic_name || 'Questions without Topic'}
                                  </span>
                                  <span className="text-sm text-gray-600 flex-shrink-0 whitespace-nowrap">
                                    {topic.completed} / {topic.total} ({Math.round(topic.percentage)}%)
                                  </span>
                                </div>
                                <Progress 
                                  value={topic.percentage} 
                                  className={cn(
                                    "h-2",
                                    topic.percentage === 100 && "bg-green-100 [&>div]:bg-green-500"
                                  )} 
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Practice Session History */}
                {analytics.recent_sessions && analytics.recent_sessions.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Practice Sessions</CardTitle>
                      <CardDescription>
                        {selectedFunctionType === 'written' ? 'Your written practice history' : 'Your last 10 practice attempts'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {analytics.recent_sessions.map((session, index) => {
                          const rawScore = selectedFunctionType === 'written' 
                            ? session.percentage 
                            : session.final_score;
                          const score = Math.round(rawScore);
                          const gradeInfo = getGrade(rawScore);
                          
                          return (
                            <div
                              key={session.id || index}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                            >
                              <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                                  score >= 60 
                                    ? 'bg-green-100 text-green-600' 
                                    : 'bg-orange-100 text-orange-600'
                                }`}>
                                  {selectedFunctionType === 'written' ? gradeInfo.grade : `${score}%`}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-700">
                                    {session.num_questions} Questions
                                    {selectedFunctionType === 'written' && session.total_marks_obtained !== undefined && (
                                      <span className="text-gray-500 ml-2">
                                        ({session.total_marks_obtained}/{session.total_marks_possible} marks)
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {new Date(session.created_at).toLocaleDateString('en-US', { 
                                      month: 'short', 
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className={`text-xl font-bold ${score >= 60 ? 'text-[#10B981]' : 'text-orange-500'}`}>
                                  {score}%
                                </div>
                                <Badge className={score >= 60 ? 'bg-green-600' : 'bg-orange-500'}>
                                  {score >= 60 ? 'Passed' : 'Practice More'}
                                </Badge>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* No Function Selected */}
            {!selectedFunction && examPlans.length > 0 && (
              <Card>
                <CardContent className="p-12 text-center">
                  <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    Select an Exam Plan
                  </h3>
                  <p className="text-gray-500">
                    Click on any exam plan above to view detailed analytics
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Add Exam Plan Dialog */}
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Exam Plan</DialogTitle>
                  <DialogDescription>
                    Set up your exam schedule for an oral or written function
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {/* Exam Type Toggle */}
                  <div>
                    <Label className="mb-2 block">Exam Type *</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={newPlan.function_type === 'oral' ? 'default' : 'outline'}
                        className={`flex-1 ${newPlan.function_type === 'oral' ? 'bg-[#8B5CF6]' : ''}`}
                        onClick={() => setNewPlan({ ...newPlan, function_type: 'oral', function_id: '' })}
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Oral
                      </Button>
                      <Button
                        type="button"
                        variant={newPlan.function_type === 'written' ? 'default' : 'outline'}
                        className={`flex-1 ${newPlan.function_type === 'written' ? 'bg-[#0A7EA4]' : ''}`}
                        onClick={() => setNewPlan({ ...newPlan, function_type: 'written', function_id: '' })}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Written
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label className="mb-2 block">Function *</Label>
                    <Select 
                      value={newPlan.function_id} 
                      onValueChange={(value) => setNewPlan({ ...newPlan, function_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select function" />
                      </SelectTrigger>
                      <SelectContent>
                        {(newPlan.function_type === 'written' ? availableWrittenFunctions : availableOralFunctions).map((func) => {
                          const suffix = func.function_suffix && func.function_suffix !== 'None' && func.function_suffix !== 'null' ? func.function_suffix : '';
                          return (
                            <SelectItem key={func.id} value={func.id}>
                              F{func.function_number}{suffix} - {func.name}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {(newPlan.function_type === 'written' ? availableWrittenFunctions : availableOralFunctions).length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        All {newPlan.function_type} functions already have exam plans
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="mb-2 block">Exam Month *</Label>
                    <MonthPicker
                      value={newPlan.exam_month}
                      onChange={(value) => setNewPlan({ ...newPlan, exam_month: value })}
                      placeholder="Select exam month"
                      fromYear={new Date().getFullYear()}
                      toYear={new Date().getFullYear() + 3}
                    />
                  </div>

                  <div>
                    <Label className="mb-2 block">Exact Date (Optional)</Label>
                    <FutureDatePicker
                      date={newPlan.exam_date ? new Date(newPlan.exam_date) : null}
                      onDateChange={(date) => setNewPlan({ ...newPlan, exam_date: date ? format(date, 'yyyy-MM-dd') : '' })}
                      placeholder="Select exam date"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave empty to use 20th of the selected month as tentative date
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleAddPlan}
                    className={newPlan.function_type === 'written' ? 'bg-[#0A7EA4] hover:bg-[#0A7EA4]/90' : 'bg-[#10B981] hover:bg-[#10B981]/90'}
                    disabled={(newPlan.function_type === 'written' ? availableWrittenFunctions : availableOralFunctions).length === 0}
                  >
                    Add Exam Plan
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
        
        <UpgradeModal 
          isOpen={showUpgrade} 
          onClose={() => setShowUpgrade(false)} 
          feature="progress-tracker"
          subscribedGrade={subscriptionStatus?.subscribed_grade}
          targetGrade={preferences.grade}
        />
      </div>
    </Layout>
  );
};

export default ProgressTrackerPage;
